import { Kampos, effects, noise, utilities } from 'kampos';
import { getEffectReveal, lerp, sineInOut } from '../utilities/motion';
const TWO_THIRDS_PI = 2 * Math.PI / 3;
const FOUR_THIRDS_PI = 4 * Math.PI / 3;
const PI_4 = Math.PI / 4;
const threeColorPreset = {
  red: {
    min: 0.0025,
    max: 0.2
  },
  green: {
    min: -0.005,
    max: -0.2
  },
  blue: {
    min: -0.0025,
    max: -0.2
  }
};
const dualColorPreset = {
  mainColor: {
    min: 0.005,
    max: 0.2
  },
  secondaryColor: {
    min: -0.0025,
    max: -0.1
  }
};
function getColorPresets(splitType) {
  if (splitType === 'all') {
    return threeColorPreset;
  }
  const {
    mainColor,
    secondaryColor
  } = dualColorPreset;
  const mainConfig = {
    min: mainColor.min,
    max: mainColor.max
  };
  const secondaryConfig = {
    min: secondaryColor.min,
    max: secondaryColor.max
  };
  switch (splitType) {
    case 'green-magenta':
      return {
        red: secondaryConfig,
        green: mainConfig,
        blue: secondaryConfig
      };
    case 'red-cyan':
      return {
        red: mainConfig,
        green: secondaryConfig,
        blue: secondaryConfig
      };
    case 'blue-yellow':
      return {
        red: secondaryConfig,
        green: secondaryConfig,
        blue: mainConfig
      };
  }
}
function calculateOffsets(splitType, angle, intensity) {
  const radians = angle * Math.PI / 180;
  const normalizedIntensity = intensity / 10;
  const preset = getColorPresets(splitType);
  const scale = (min, max) => min + (max - min) * normalizedIntensity;
  const [red, green, blue] = ['red', 'green', 'blue'].map(color => Math.abs(scale(preset[color].min, preset[color].max)));
  if (splitType === 'all') {
    return {
      red: {
        x: red * Math.cos(radians),
        y: red * Math.sin(radians)
      },
      green: {
        x: green * Math.cos(radians + TWO_THIRDS_PI),
        y: green * Math.sin(radians + TWO_THIRDS_PI)
      },
      blue: {
        x: blue * Math.cos(radians + FOUR_THIRDS_PI),
        y: blue * Math.sin(radians + FOUR_THIRDS_PI)
      }
    };
  }
  const cos = Math.cos(radians);
  const sin = Math.sin(radians);
  switch (splitType) {
    case 'green-magenta':
      return {
        red: {
          x: red * cos,
          y: red * sin
        },
        green: {
          x: green * Math.cos(radians + PI_4),
          y: green * Math.sin(radians + PI_4)
        },
        blue: {
          x: blue * cos,
          y: blue * sin
        }
      };
    case 'red-cyan':
      return {
        red: {
          x: red * Math.cos(radians + PI_4),
          y: red * Math.sin(radians + PI_4)
        },
        green: {
          x: green * cos,
          y: green * sin
        },
        blue: {
          x: blue * cos,
          y: blue * sin
        }
      };
    case 'blue-yellow':
      return {
        red: {
          x: red * cos,
          y: red * sin
        },
        green: {
          x: green * cos,
          y: green * sin
        },
        blue: {
          x: blue * Math.cos(radians + PI_4),
          y: blue * Math.sin(radians + PI_4)
        }
      };
  }
}
export default async function create(target, options) {
  var _options$namedEffect;
  const {
    autoplay = true,
    isTriggeredByMouse = false,
    radius = 0.3,
    frequency = 1,
    octaves = 2,
    splitType = 'all',
    angle: initialAngle = 90,
    intensity: initialIntensity = 0.63
  } = options.namedEffect;
  let angle = initialAngle;
  let intensity = initialIntensity;
  let scroll = 1;
  let {
    speed = 5
  } = options.namedEffect;
  const offsets = calculateOffsets(splitType, angle, intensity * scroll);
  const FREQUENCY = frequency / target.width;
  const _frequency = {
    x: FREQUENCY,
    y: FREQUENCY
  };
  const circle = utilities.circle();
  const mouseUtil = utilities.mouse({
    initial: {
      x: -0.5,
      y: -0.5
    }
  });
  const resolution = utilities.resolution({
    width: target.width,
    height: target.height
  });
  const render = {
    fragment: {
      uniform: {
        u_mouseEnabled: 'bool',
        u_mouseOnly: 'bool',
        u_radius: 'float',
        u_intensity: 'float'
      },
      main: `
    if (u_mouseEnabled) {
        float ar_ = u_resolution.x / u_resolution.y;
        vec2 st_ = gl_FragCoord.xy / u_resolution;
        float circle_ = circle(vec2(st_.x * ar_, st_.y), vec2(u_mouse.x * ar_, u_mouse.y), u_radius, 0.3) * u_intensity;
        if (u_mouseOnly) {
          turbulenceValue *= circle_;
        } else {
          turbulenceValue += turbulenceValue * circle_;
        }
    }`
    },
    uniforms: [{
      name: 'u_mouseEnabled',
      type: 'i',
      data: [Number(isTriggeredByMouse)]
    }, {
      name: 'u_radius',
      type: 'f',
      data: [radius]
    }, {
      name: 'u_intensity',
      type: 'f',
      data: [intensity * scroll]
    }, {
      name: 'u_mouseOnly',
      type: 'i',
      data: [+(((_options$namedEffect = options.namedEffect) == null ? void 0 : _options$namedEffect.type) === 'RGBOrb')]
    }],
    get mouseEnabled() {
      return !!this.uniforms[0].data[0];
    },
    set mouseEnabled(enabled) {
      this.uniforms[0].data[0] = Number(enabled);
    },
    get radius() {
      return this.uniforms[1].data[0];
    },
    set radius(r) {
      this.uniforms[1].data[0] = r;
    },
    get intensity() {
      return this.uniforms[2].data[0];
    },
    set intensity(i) {
      this.uniforms[2].data[0] = i;
    },
    get mouseOnly() {
      return !!this.uniforms[3].data[0];
    },
    set mouseOnly(b) {
      this.uniforms[3].data[0] = +b;
    }
  };
  const channelSplit = effects.channelSplit({
    offsetRed: offsets.red,
    offsetGreen: offsets.green,
    offsetBlue: offsets.blue,
    ...(autoplay || isTriggeredByMouse ? {
      offsetInputR: `turbulenceValue * u_channelOffsetR`,
      offsetInputG: `turbulenceValue * u_channelOffsetG`,
      offsetInputB: `turbulenceValue * u_channelOffsetB`,
      boundsOffsetFactor: boundsOffset => `exp(5.0 * ${boundsOffset})`
    } : {})
  });
  const turbulence = effects.turbulence({
    noise: noise.simplex,
    frequency: _frequency,
    octaves,
    isFractal: true
  });
  const FADE_FACTOR = 0.9;
  function calculateIntensity(velocity) {
    const target_ = instance.config.target;
    return Math.sqrt((velocity.x / target_.width) ** 2 + (velocity.y / target_.height) ** 2);
  }
  let updateTime;
  if (autoplay) {
    updateTime = time => {
      turbulence.time = time * speed;
    };
  }
  const instance = new Kampos({
    effects: [resolution, mouseUtil, turbulence, circle, render, channelSplit],
    target
  });
  let _revealEffect;
  return {
    instance,
    play(_temp) {
      let {
        reveal,
        beforeDraw
      } = _temp === void 0 ? {} : _temp;
      if (reveal) {
        _revealEffect = getEffectReveal(progress => {
          this.setters.intensity(lerp(0, initialIntensity, sineInOut(progress)));
        });
      }
      this.instance.play(beforeDraw);
    },
    beforeDraw(t) {
      updateTime == null || updateTime(t);
      if (_revealEffect) {
        const done = _revealEffect(t);
        if (done) {
          _revealEffect = undefined;
        }
      }
    },
    setters: {
      speed(factor) {
        speed = factor;
      },
      frequency(f) {
        const target_ = instance.config.target;
        turbulence.frequency = {
          x: f / target_.width,
          y: f / target_.width
        };
      },
      octaves(o) {
        turbulence.octaves = o;
      },
      angle(a) {
        const {
          red,
          green,
          blue
        } = calculateOffsets(splitType, a, intensity);
        channelSplit.red = red;
        channelSplit.green = green;
        channelSplit.blue = blue;
        angle = a;
      },
      intensity(i) {
        const {
          red,
          green,
          blue
        } = calculateOffsets(splitType, angle, i * scroll);
        channelSplit.red = red;
        channelSplit.green = green;
        channelSplit.blue = blue;
        intensity = i;
      },
      scroll(s) {
        const {
          red,
          green,
          blue
        } = calculateOffsets(splitType, angle, intensity * s);
        channelSplit.red = red;
        channelSplit.green = green;
        channelSplit.blue = blue;
        scroll = s;
      },
      toggleMouse(b) {
        render.mouseEnabled = b;
      },
      toggleMouseOnly(b) {
        render.mouseOnly = b;
      },
      radius(r) {
        render.radius = r;
      },
      mouse(p) {
        mouseUtil.position = p;
      },
      mouseIntensity(p) {
        if (p.active === false) {
          const currentV = render.intensity;
          render.intensity = currentV > 0.01 ? currentV * FADE_FACTOR : 0;
        } else {
          render.intensity = calculateIntensity(p.v);
        }
      }
    },
    resize(w, h) {
      resolution.resolution = {
        width: w,
        height: h
      };
      turbulence.frequency = {
        x: frequency / w,
        y: frequency / h
      };
      instance.setSource({
        width: w,
        height: h
      });
    }
  };
}
//# sourceMappingURL=RGBSplit.js.map